/**
 * Phone Authentication Manager for account settings
 * Handles phone verification flow with reCAPTCHA for PersonalInfoPage
 */

import React, { useState, useEffect, useRef } from 'react';
import { getAuth, signInWithPhoneNumber, ConfirmationResult } from 'firebase/auth';
import { toast } from 'react-toastify';
import PhoneVerificationModal from '../components/PhoneVerificationModal';
import { formatPhoneToE164 } from '../utils/phoneFormatting';
import { isValidE164Format } from '../utils/phoneValidation';
import { initializeRecaptcha, cleanupRecaptcha, renderRecaptcha } from '../utils/recaptchaSetup';
import { getAuthErrorMessage } from '../utils/smsErrors';
import DataOps from '../../data/DataOps';

interface PhoneAuthManagerProps {
  /** Firebase app instance */
  firebaseApp: any;
  /** User object to update after verification */
  user: any;
  /** Callback to update user state */
  onUserUpdate: (user: any) => void;
  /** Callback to update profile data */
  onProfileDataUpdate?: (phone: string) => void;
  /** Optional container ID for reCAPTCHA (defaults to 'recaptcha-container-phone-auth') */
  recaptchaContainerId?: string;
}

/**
 * Hook to manage phone verification state and flow
 */
export const usePhoneAuth = ({
  firebaseApp,
  user,
  onUserUpdate,
  onProfileDataUpdate,
  recaptchaContainerId = 'recaptcha-container-phone-auth',
}: PhoneAuthManagerProps) => {
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isPhoneVerifying, setIsPhoneVerifying] = useState(false);
  const [pendingPhoneE164, setPendingPhoneE164] = useState('');
  const [confirmationResultRef, setConfirmationResultRef] = useState<ConfirmationResult | null>(null);
  const recaptchaRef = useRef<any>(null);

  // Initialize reCAPTCHA for phone verification
  useEffect(() => {
    if (typeof window === 'undefined') return; // Only run on web

    const initializeRecaptchaWithRetry = () => {
      const container = document.getElementById(recaptchaContainerId);
      if (!container) {
        setTimeout(initializeRecaptchaWithRetry, 100);
        return;
      }

      if (!recaptchaRef.current) {
        recaptchaRef.current = initializeRecaptcha(firebaseApp, {
          containerId: recaptchaContainerId,
          size: 'invisible',
        });
      }
    };

    // Initialize with a delay to ensure DOM is ready
    setTimeout(initializeRecaptchaWithRetry, 300);

    return () => {
      cleanupRecaptcha(recaptchaRef.current);
      recaptchaRef.current = null;
    };
  }, [firebaseApp, recaptchaContainerId]);

  /**
   * Initiates phone verification by sending SMS
   */
  const handlePhoneVerification = async (phoneNumber: string) => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    const e164Phone = formatPhoneToE164(phoneNumber);
    
    if (!isValidE164Format(e164Phone)) {
      toast.error('Please enter a valid phone number');
      return;
    }

    // Open modal immediately
    setPendingPhoneE164(e164Phone);
    setShowPhoneVerification(true);
    setIsPhoneVerifying(true);

    try {
      const auth = getAuth(firebaseApp);
      
      // Check if reCAPTCHA is ready
      if (!recaptchaRef.current) {
        toast.error('reCAPTCHA not ready. Please try again.');
        setIsPhoneVerifying(false);
        setShowPhoneVerification(false);
        return;
      }
      
      // Ensure reCAPTCHA is rendered
      await renderRecaptcha(recaptchaRef.current);
      
      const confirmationResult = await signInWithPhoneNumber(auth, e164Phone, recaptchaRef.current);
      setConfirmationResultRef(confirmationResult);
      toast.success('Verification code sent to your phone');
    } catch (error: any) {
      const msg = getAuthErrorMessage(error?.code, 'Failed to send verification code. Please try again.');
      toast.error(msg);
      setShowPhoneVerification(false);
    } finally {
      setIsPhoneVerifying(false);
    }
  };

  /**
   * Verifies the code entered by the user
   */
  const handleVerifyPhone = async () => {
    if (!verificationCode.trim()) {
      toast.error('Please enter the verification code');
      return;
    }
    if (!confirmationResultRef) {
      toast.error('No verification session found');
      return;
    }

    setIsPhoneVerifying(true);
    try {
      await confirmationResultRef.confirm(verificationCode);
      
      // Update the user's phone number and verification status
      if (user) {
        const updatedUser = {
          ...user,
          phone: pendingPhoneE164,
          phoneVerified: true,
          primaryContactMethod: 'phone'
        };
        
        // Save to the correct collection
        const collectionName = DataOps.getCollectionNameForAccountType(user.accountType);
        await DataOps.saveCustomDataToFirebase(collectionName, user.userId, updatedUser, true);
        await DataOps.saveDataToStorage('user', updatedUser);
        onUserUpdate(updatedUser);
        
        // Update profile data if callback provided
        if (onProfileDataUpdate) {
          onProfileDataUpdate(pendingPhoneE164);
        }
        
        toast.success('Phone number verified successfully!');
        setShowPhoneVerification(false);
        setVerificationCode('');
        setConfirmationResultRef(null);
      }
    } catch (error: any) {
      const msg = getAuthErrorMessage(error?.code, 'Invalid verification code');
      toast.error(msg);
    } finally {
      setIsPhoneVerifying(false);
    }
  };

  /**
   * Resends the verification code
   */
  const handleResendPhoneCode = async () => {
    if (pendingPhoneE164) {
      await handlePhoneVerification(pendingPhoneE164);
    }
  };

  /**
   * Closes the verification modal
   */
  const handleCloseVerification = () => {
    setShowPhoneVerification(false);
    setVerificationCode('');
    setConfirmationResultRef(null);
  };

  return {
    showPhoneVerification,
    verificationCode,
    setVerificationCode,
    isPhoneVerifying,
    pendingPhoneE164,
    confirmationResultRef,
    handlePhoneVerification,
    handleVerifyPhone,
    handleResendPhoneCode,
    handleCloseVerification,
  };
};

/**
 * Phone Authentication Manager Component
 * Renders the reCAPTCHA container and verification modal
 */
interface PhoneAuthManagerComponentProps extends PhoneAuthManagerProps {
  children?: React.ReactNode;
}

const PhoneAuthManager: React.FC<PhoneAuthManagerComponentProps> = ({
  firebaseApp,
  user,
  onUserUpdate,
  onProfileDataUpdate,
  recaptchaContainerId = 'recaptcha-container-phone-auth',
  children,
}) => {
  const phoneAuth = usePhoneAuth({
    firebaseApp,
    user,
    onUserUpdate,
    onProfileDataUpdate,
    recaptchaContainerId,
  });

  return (
    <>
      {children}
      
      {/* reCAPTCHA container */}
      <div id={recaptchaContainerId} style={{height: 0, width: 0, overflow: 'hidden'}}></div>

      {/* Phone verification modal */}
      <PhoneVerificationModal
        visible={phoneAuth.showPhoneVerification}
        onClose={phoneAuth.handleCloseVerification}
        onVerify={phoneAuth.handleVerifyPhone}
        onResend={phoneAuth.handleResendPhoneCode}
        isLoading={phoneAuth.isPhoneVerifying}
        verificationCode={phoneAuth.verificationCode}
        setVerificationCode={phoneAuth.setVerificationCode}
        phoneE164={phoneAuth.pendingPhoneE164}
      />
    </>
  );
};

export default PhoneAuthManager;

